import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { getCurrentParticipantSelector } from "@/features/participants/api/get-current-participant";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { useMicrophoneActivity } from "../../vidyo-devices/api/use-microphone-activity";

import { lowerHand } from "./toggle-hand";

const WARNING_DELAY = 5000; // 5 seconds in milliseconds

export function useAutoLowerHand() {
  const isHandRaised =
    useVidyoStore(getCurrentParticipantSelector)?.isHandRaised || false;

  const isMicrophoneMuted = useVidyoStore(
    (state) => state.microphone.shouldBeMuted,
  );

  const [isWarning, setIsWarning] = useState(false);
  const keepRaisedRef = useRef(false);
  const warningTimeoutRef = useRef<NodeJS.Timeout>();

  const { isActive } = useMicrophoneActivity({
    threshold: 0.06,
    enabled: isHandRaised && !isWarning,
  });

  const handleLowerHand = useCallback(() => {
    lowerHand();
    toast.success("Your hand has been lowered");
  }, []);

  useEffect(() => {
    // Reset state and dismiss any existing toast when hand is lowered
    if (!isHandRaised) {
      setIsWarning(false);
      keepRaisedRef.current = false;
      //we lowered the hand, so we don't need to show the warning
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = undefined;
      }
      toast.dismiss();
      return;
    }

    // Only show warning if hand is raised, mic is active and not muted, and we're not already warning
    if (
      isHandRaised &&
      isActive &&
      !isWarning &&
      !keepRaisedRef.current &&
      !isMicrophoneMuted &&
      !warningTimeoutRef.current
    ) {
      // Set new timeout for warning
      warningTimeoutRef.current = setTimeout(() => {
        setIsWarning(true);
        toast(
          "It sounds like you've said something, so your hand will be lowered.",
          {
            duration: 10000,
            onDismiss: () => {
              setIsWarning(false);
            },
            onAutoClose: () => {
              if (!keepRaisedRef.current) {
                handleLowerHand();
              }
            },
            action: {
              label: "Keep it raised",
              onClick: () => {
                keepRaisedRef.current = true;
                setIsWarning(false);
                toast.success("Your hand will stay raised");
              },
            },
          },
        );
      }, WARNING_DELAY);
    }
  }, [isHandRaised, isActive, isWarning, handleLowerHand, isMicrophoneMuted]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);
}
